import { Fragment, useCallback, useContext, useEffect } from "react";

import { useState } from "react";
import { useApoAuth } from "../../../hooks";
import { GlobalModalsContext } from "../../../context";
import { StyleTypeContext } from "../../../umbilical_tools";
import { useSelector } from "react-redux";
import { InlineIcon } from "@iconify/react/dist/iconify.js";
import { UploadQuotationRequestDoc } from "./UploadFileForm";
import { InitialSetup } from "./InitialSetup";
import { CancelButton, CloseButton } from "./componets";
import { QuotationRequestForm } from "./QuotationRequestForm";
import { QuotationRequestChat } from "./QuotationRequestChat";
import { SubmittingQuotationRequest } from "./SubmittingQuotationRequest";
import { submitQuotationRequest } from "../../../api/quotation";
import { useMutation } from "@tanstack/react-query";
import { SuccessfulQuotationSubmission } from "./SuccessfulQuotationSubmission";
import { toast } from "react-toastify";

type UserType = {
  "https://sisitrip.com": {
    mark: {
      complete_user_profile: boolean;
      complete_company_profile: string;
      company_id: number;
      company_type: string;
    };
  };
  nickname: string;
  name: string;
  picture: string;
  updated_at: string;
  email: string;
  email_verified: boolean;
  sub: string;
};

export function NewQuotationRequestModal() {
  const [file, setFile] = useState<File | null>(null);
  const [parsedFile, setParsedFile] = useState<string>("");
  const [quotationRequestReference, setQuotationRequestReference] = useState<string>("");

  const [step, setStep] = useState<
    | "initial"
    | "upload"
    | "parsing_document"
    | "preview_document"
    | "preview"
    | "quotation_request_form"
    | "chat"
    | "submitting_quotation_request"
    | "successful_quotation_submission"
  >("initial");

  const { user, getIdTokenClaims } = useApoAuth();
  const usr = user as UserType;

  const sourceEntity = `${usr["https://sisitrip.com"]?.mark?.company_id}___${usr["https://sisitrip.com"]?.mark?.company_type}`;

  const { setShowNewQuotationRequestModal } = useContext(GlobalModalsContext) as {
    setShowNewQuotationRequestModal: (show: boolean) => void;
  };
  const { brand } = useContext(StyleTypeContext);

  const onCancel = useCallback(() => {
    setShowNewQuotationRequestModal(false);
  }, [setShowNewQuotationRequestModal]);

  const { source_entity: target_entity } = useSelector(
    (state: { WebsiteMetadata: { metadata: { source_entity: string } } }) => {
      const source_entity = state?.WebsiteMetadata?.metadata?.source_entity;
      return { source_entity };
    },
  );

  const finalizeQRMutation = useMutation({
    mutationFn: async ({ session_id }: { session_id: string }) => {
      const token: { __raw: string } = await getIdTokenClaims();

      return submitQuotationRequest({ session_id, token: token?.__raw });
    },
  });

  // Handle the successful quotation request submission
  useEffect(() => {
    if (!finalizeQRMutation.isSuccess) return;
    setQuotationRequestReference(finalizeQRMutation.data.data.quotation_request_reference);
    setStep("successful_quotation_submission");
  }, [finalizeQRMutation.isSuccess, finalizeQRMutation.data]);

  // Handle the error when submitting the quotation request
  useEffect(() => {
    if (!finalizeQRMutation.isError) return;
    toast.error("Failed to submit quotation request");
    setStep("quotation_request_form");
  }, [finalizeQRMutation.isError]);

  // Reset the state when the step is changed to initial
  useEffect(() => {
    if (step !== "initial") return;
    setParsedFile("");
    setFile(null);
  }, [step]);

  let modalWidth = "";
  if (step === "upload") {
    modalWidth = "w-1/4!";
  } else if (step === "chat") {
    modalWidth = "w-1/2!";
  }

  return (
    <div className="Modal QuotationRequestModal">
      <div
        className={`Modal__card QuotationRequestModal__container min-h-1/2 max-h-4/5 ${modalWidth} max-w-2/3 overflow-y-hidden grid grid-rows-[auto_1fr_auto]`}
      >
        <div
          className="Modal__card__header QuotationRequestModal__container__header"
          data-brand={brand}
        >
          <h6>{step === "chat" ? "MIKI Plus Trip Assistant" : "Quotation Request"}</h6>
          <InlineIcon
            id="closeDeepHotelSearchForm"
            icon="ei:close"
            className="QuotationRequestModal__container__header__closeIcon"
            onClick={() => setShowNewQuotationRequestModal(false)}
          />
        </div>
        {step === "initial" && (
          <Fragment>
            <div className="Modal__card__body">
              <InitialSetup
                onUpload={() => setStep("upload")}
                onFillForm={() => setStep("quotation_request_form")}
                onChat={() => setStep("chat")}
              />
            </div>
            <div className="Modal__card__actions">
              <CancelButton onCancel={onCancel} />
            </div>
          </Fragment>
        )}
        {step === "upload" && (
          <UploadQuotationRequestDoc
            file={file}
            onFileChange={setFile}
            onBack={() => setStep("initial")}
            onSetParsedFile={(parsedFile) => {
              setParsedFile(parsedFile);
              setStep("quotation_request_form");
            }}
          />
        )}
        {step === "quotation_request_form" && (
          <QuotationRequestForm
            source_entity={sourceEntity}
            target_entity={target_entity}
            initialMessage={parsedFile}
            onBack={() => setStep("initial")}
            finalizeQuotationRequest={(sessionId: string) => {
              setStep("submitting_quotation_request");
              finalizeQRMutation.mutate({ session_id: sessionId });
            }}
          />
        )}
        {step === "preview_document" && (
          <Fragment>
            <div className="Modal__card__body overflow-hidden">
              <h6>Preview Document</h6>
              <div className="grid border border-gray-200 rounded-md p-3 overflow-auto">
                <p className="whitespace-pre">{parsedFile}</p>
              </div>
            </div>
            <div className="Modal__card__actions">
              <CancelButton onCancel={onCancel} />
            </div>
          </Fragment>
        )}
        {step === "chat" && (
          <QuotationRequestChat
            onBack={() => setStep("initial")}
            onGenerateQuotation={(message) => {
              setParsedFile(message);
              setStep("quotation_request_form");
            }}
          />
        )}
        {step === "submitting_quotation_request" && <SubmittingQuotationRequest />}
        {step === "successful_quotation_submission" && (
          <Fragment>
            <SuccessfulQuotationSubmission reference={quotationRequestReference} />
            <div className="Modal__card__actions">
              <CloseButton onClose={onCancel} />
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
}
